/**
 * useRoleList Hook
 * React Query hook for fetching roles list
 *
 * @module domains/personnel/presentation/hooks/useRoleList
 */

import { useQuery, UseQueryResult } from 'react-query';
import { getRoleUseCases } from '@personnel/composition';
import type { Role } from '@personnel/domain/entities/Role';

/**
 * Options for useRoleList hook
 */
export interface UseRoleListOptions {
  enabled?: boolean;
}

/**
 * Return type for useRoleList hook
 */
export interface UseRoleListResult {
  roles: Role[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Query keys for roles
 */
export const ROLES_QUERY_KEYS = {
  all: ['roles'] as const,
  list: () => ['roles', 'list'] as const,
  detail: (uuid: string) => ['roles', 'detail', uuid] as const,
};

/**
 * Hook for fetching all roles (no pagination)
 *
 * Uses React Query for caching, refetching, and state management.
 * Use Case is obtained from Composition Root (@personnel/composition).
 *
 * @param {UseRoleListOptions} options - Configuration options
 * @returns {UseRoleListResult} Roles data and query state
 *
 * @example
 * // Basic usage
 * const { roles, isLoading } = useRoleList();
 *
 * @example
 * // Disabled until needed
 * const { roles, refetch } = useRoleList({ enabled: false });
 * // Later...
 * refetch();
 *
 * @example
 * // Testing with mock composition
 * jest.mock('@personnel/composition', () => ({
 *   roleUseCases: {
 *     getRoles: { execute: jest.fn().mockResolvedValue([mockRole]) }
 *   }
 * }));
 */
export const useRoleList = (options: UseRoleListOptions = {}): UseRoleListResult => {
  const { enabled = true } = options;

  const query: UseQueryResult<Role[], Error> = useQuery({
    queryKey: ROLES_QUERY_KEYS.list(),
    queryFn: () => getRoleUseCases().getRoles.execute(), // ✅ Uses composed Use Case
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  return {
    roles: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error || null,
    refetch: query.refetch,
  };
};
